<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

  import Stepper from '../Stepper/Stepper.svelte';
  import StepperNavigator from '../Stepper/StepperNavigator.svelte/';
  import { onMount, afterUpdate } from 'svelte';
  import {
    energyTypeOptions,
    mechanismOptions,
    allControls,
    controlOptions,
    iconOptions,
    hazard as hazardStore,
    relatedActivities,
    control as controlStore
  } from './store.js';
  import Modal from '../Modal.svelte';
  import Button from '../Button.svelte';
  import {validateForm} from '../Form/utils.js';
  import {get} from '../../utils.js';
  import TextInput from '../TextInput.svelte';
  import DuplicateCheckTextInput from '../DuplicateCheckTextInput.svelte';
  import ComboSelect from '../ComboSelect/ComboSelect.svelte';
  import GlobalControlSelect from '../ComboSelect/GlobalControlSelect.svelte';
  import FocusQuestionInput from './FocusQuestionInputV2.svelte';
  import HazardDetail from './HazardDetailV2.svelte';
  import RelatedActivities from './RelatedActivities.svelte';
  import Errors from '../Errors.svelte';
  import ControlInlineForm from './ControlInlineFormV2.svelte';
  import {submit} from '../Form/utils.js';
  import yup, {object, string, number} from 'yup';
  import {sgettext} from '../../i18n.js';


  export let title = sgettext('$$GLOBAL_RISK$$');
  export let id = 'globalHazardForm';
  export let duplicateCheckApi = `${PATH_PREFIX}/sw/api/globalhazard/check_duplicate/`;
  export let energyTypeOptionApi = `${PATH_PREFIX}/sw/api/energy_types/`;
  export let mechanismOptionApi = `${PATH_PREFIX}/sw/api/mechanisms/`;
  export let controlOptionApi = `${PATH_PREFIX}/sw/api/globalcontrols/`;
  export let iconOptionApi = `${PATH_PREFIX}/sw/api/icons/`;
  export let actionUrl = `${PATH_PREFIX}/sw/api/globalhazard/`;
  export let controlFormUrl = `${PATH_PREFIX}/sw/api/globalcontrol/`;
  export let relatedActivitiesApi = `${PATH_PREFIX}/sw/api/affected-activities/`;
  export let onSubmit = (data) => data;
  export let editId = 0;
  export let currentStep = 1;
  export let controlDuplicateCheckApi = `${PATH_PREFIX}/sw/api/globalcontrol/check_duplicate/`;
  export let isEcmsBranch = false;
  export let energyTypesFilter = false;
  export let ecmsEnergyTypeId = -1;
  export let ecmsMechanismId = -1;
  export let ecmsIconId = -1;

  let isValid = true;
  let showControlForm = false;
  let controlForm;
  let hazardNameBeforeEdit = '';
  let duplicateCheckHardFail = false;
  let hazardDetail;
  let addAnother = false;

  let mechanismSchema = object().shape({
    mechanism_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$')),
  });
  
  let schema = object().shape({
    name: string()
          .required(sgettext('$$REQUIRED_FIELD$$'))
          .test(
            sgettext("$$DUPLICATE_1$$"),
            sgettext("$$RISK_NAME_ALREADY_EXISTS$$"),
            _ => !duplicateCheckHardFail,
        ),
    mechanism_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$')),
    icon_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$'))
  });

  // For ECMS, icon is prefilled
  let schemaForEcms = object().shape({
    name: string()
          .required(sgettext('$$REQUIRED_FIELD$$'))
          .test(
            sgettext("$$DUPLICATE_1$$"),
            sgettext("$$RISK_NAME_ALREADY_EXISTS$$"),
            _ => !duplicateCheckHardFail,
        ),
    mechanism_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$')),
    icon_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$'))
  });

  let controlSchema = object().shape({
    description: string().required(),
    name: string().required(),
    icon_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$'))
  });

  let errors = null;
  let sending = false;

  export let hazard = {
    name: '',
    description: '',
    energy_type: '',
    mechanism_id: '',
    icon_id: '',
    focus_questions: [],
    controls: [],
  };

  let steps = [
    {
      order: 1,
      label: sgettext('$$ENERGY$$'),
      done: false,
      isValid: true,
      fieldIsValid: {
        mechanism_id: {
          isValid: true,
          errors: ''
        },
      }
    },
    {
      order: 2,
      label: sgettext('$$RISK_DETAIL$$'),
      done: false,
      isValid: true,
      fieldIsValid: {
        name: {
          isValid: true,
          errors: ''
        },
        description: {
          isValid: true,
          errors: ''
        },
        icon_id: {
          isValid: true,
          errors: ''
        }
      }
    },
    {
      order: 3,
      label: sgettext('$$CONTROLS$$'),
      done: false,
      isValid: false,
      fieldIsValid: {
        description: {
          isValid: true,
          errors: ''
        },
        name: {
          isValid: true,
          errors: ''
        },
        icon_id: {
          isValid: true,
          errors: ''
        }
      }
    },
    {
      order: 4,
      label: sgettext('$$REVIEW$$'),
      done: false,
      isValid: false
    },
  ];

  let finalStep = 4;

  hazardStore.set(hazard);

  let hidePopup = true;
  let data = [];

  // Only want to show expired global controls as an option when editing an item to allow them to *remove* the expired
  // control.
  // For creation, we block expired global controls from being selected.
  $: if (editId) {
    getEditItem();
    controlOptions.set($allControls);
  } else {
    controlOptions.set([...$allControls.filter(x => !x.expired)])
  }

  $: {
      // ensure that a control can only be added once
      $hazardStore.controls = [...new Set($hazardStore.controls)];
  }

  async function handleFormValidation() {
    sending = true;
    try{
      if(currentStep === 3 && showControlForm) {
        //validate control form
        await validateForm(controlSchema, $controlStore)
      } else if(currentStep === 2) {
        if ($hazardStore.mechanism_id === ecmsMechanismId) {
          await validateForm(schemaForEcms, $hazardStore);
        } else {
          await validateForm(schema, $hazardStore);
        }
        isValid = true;
        getRelatedActivities();
      } else if(currentStep === 1) {
        await validateForm(mechanismSchema, $hazardStore);
        isValid = true;
      }

      updateStepStatus(isValid);
      if(currentStep === finalStep){
        handleSave();
      } else {
        currentStep = currentStep + 1;
      }

      if(currentStep > finalStep){
        currentStep = finalStep;
      }

      hazardDetail = getHazardDetail();
    } catch(err) {
      isValid = false;
      updateStepStatus(isValid);
      err.inner.forEach(er => {
        steps[currentStep-1].fieldIsValid[er.path].isValid = false;
        steps[currentStep-1].fieldIsValid[er.path].errors = er.message;
      });
    }
    sending = false;
  }

  function updateStepStatus(valid) {
    const current = steps.filter(s => s.order === currentStep);
    if (current.length) {
      current[0].isValid = valid;
    }
  }

  function handleShowControlForm(){
      showControlForm = !showControlForm;
      if(!showControlForm) {
        isValid = true;
      }
      jQuery('#' + controlFormModalId).modal('show');
  }

  function resetControlForm() {
    // controlForm.show = false;
    try {
      controlForm.resetForm();
    } catch (_) {}
  }

  function resetValidation() {
    for (let i = 0; i < 3; i++) {
      Object.keys(steps[i].fieldIsValid).forEach(k => {
        steps[i].fieldIsValid[k].isValid = true;
        steps[i].fieldIsValid[k].errors = '';
      })
    }
  }

  function handleClose() {
    let hazard = {
      name: '',
      energy_type: '',
      mechanism: '',
      icon_id: '',
      focus_questions: [],
      controls: [],
    };

    let control = {
      description: '',
      icon_id: ''
    }

    hazardStore.set(hazard);
    controlStore.set(control);
    hidePopup = true;
    currentStep = 1;
    editId = 0;
    isValid = true;
    sending = false;
    showControlForm = false;
    errors = '';
    relatedActivities.set([])
  }

  function getHazardDetail() {
    const detail = {
      name: $hazardStore.name,
      description: $hazardStore.hazard,
      icon: $iconOptions.filter((i) => i.id === $hazardStore.icon_id)[0],
      focus_questions: $hazardStore.focus_questions,
    };

    let energy = '';
    let mechanism = '';

    try {
      energy = $energyTypeOptions.filter(x => x.id === $hazardStore.energy_type)[0];
      mechanism = $mechanismOptions.filter(x => x.id === $hazardStore.mechanism_id)[0];
    } catch (_) {}

    detail.energy = energy;
    detail.mechanism = mechanism;
    detail.controls = $controlOptions.filter((c) =>
      $hazardStore.controls.includes(c.id)
    );

    return detail;
  }


  function handleDuplicate(event) {
    isValid = true;
    duplicateCheckHardFail = false;
    if (event.detail.length) {
        if (event.detail[0].text !== hazardNameBeforeEdit && event.detail[0].similarity === 100) {
            isValid = false;
            duplicateCheckHardFail = true;
        }
    }
  }

  async function getEditItem() {
    if (editId) {
      const response = await get(actionUrl + editId + '/');
      const json = await response.json();

      if (json) {
        hazardNameBeforeEdit = json.name;
        json.energy_type = json.mechanism.energy_type.id;
        json.controls = json.controls.filter(c => c);
        hazardStore.set(json);

        await getMechanismOptions();
      }

      return json;
    }
  }

  async function getEnergyTypes(url) {
    const response = await get(url);
    const json = await response.json();

    if(energyTypesFilter){
        energyTypeOptions.set(json.filter(x => energyTypesFilter.includes(x.name)))
    }else{
        energyTypeOptions.set(json);
    }

  }

  async function getMechanismOptions() {

    if ($hazardStore.energy_type) {
      let selectedEnergyType = $energyTypeOptions.filter(x => x.id === $hazardStore.energy_type);
      if (selectedEnergyType.length === 1) {
        mechanismOptions.set(selectedEnergyType[0].mechanisms);

        // clear the selected mechanism if the energy type is changed
        if ($mechanismOptions.filter(x => x.id === $hazardStore.mechanism_id).length === 0) {
          $hazardStore.mechanism_id = null;
        }

        if ($mechanismOptions.length === 1) {
          $hazardStore.mechanism_id = $mechanismOptions[0].id;
        }
      }
    }
    resetValidation();

  }

  async function getIcons(url) {
    const response = await get(url);
    const json = await response.json();

    iconOptions.set(json);
  }

  async function getControls(url) {
    const response = await get(url);
    const json = await response.json();

    allControls.set(json);
    controlOptions.set(json);
  }


  async function getRelatedActivities() {
    if(editId > 0) {
      let hazardId = editId;
      const response = await get(relatedActivitiesApi + hazardId + '/');
      const json = await response.json();

      /* This fetch can take a really long time and it is possible that the user will have moved on to editing
         a different hazard. We need to check that we're indeed still on the same hazard before displaying
         the results.
      */

      if (hazardId === editId) {
        relatedActivities.set(json);
      }
    }
  }

  async function prefillEcmsFields() {
    if (isEcmsBranch && !editId) {
      $hazardStore.energy_type = ecmsEnergyTypeId;
      await getMechanismOptions();

      $hazardStore.mechanism_id = ecmsMechanismId;
      $hazardStore.icon_id = ecmsIconId;
      currentStep = 2;
    }
  }

  onMount(async () => {
    await getEnergyTypes(energyTypeOptionApi);
    // await getMechanisms(mechanismOptionApi);
    await getIcons(iconOptionApi);
    await getControls(controlOptionApi);
    hazardDetail = getHazardDetail();
    await prefillEcmsFields();


    jQuery('#' + id).on('show.bs.modal', function() {
      resetValidation();
    });

    jQuery('#' + id).on('shown.bs.modal', function() {
      jQuery('#name').trigger('focus');
    });

    jQuery('#' + id).on('hidden.bs.modal', function() {
      handleClose();
    });

    jQuery('#' + controlFormModalId).on('show.bs.modal', function() {
    });

    jQuery('#' + controlFormModalId).on('shown.bs.modal', function() {
      jQuery('#name').trigger('focus');
    });

    jQuery('#' + controlFormModalId).on('hidden.bs.modal', function() {
      resetControlForm();
    });

  });

  function handleSave() {
    sending = true;
    let method = 'POST';
    let url = actionUrl;
    if(editId) {
      method = 'PUT';
      url = actionUrl + editId + '/';
    }

    submit(url, method, getPayload())
      .then((data) => {
        if (addAnother) {
          resetValidation();

          hazardStore.set({
            name: '',
            description: '',
            energy_type: '',
            mechanism_id: '',
            icon_id: '',
            focus_questions: [],
            controls: [],
            ppe: false
          });
          editId = null;
          currentStep = 1;
          addAnother = false;
          mechanismOptions.set([]);
          prefillEcmsFields();
          relatedActivities.set([]);
        } else {
          onSubmit(data);
        }
        sending = false;
      })
      .catch((error) => {
        errors = error;
        sending = false;
      });
  }

  function getPayload(){
    const payload = {...$hazardStore, focus_questions: []};

    if($hazardStore.focus_questions){
      $hazardStore.focus_questions.forEach(q => {
        if(q.hasOwnProperty('name') && q.name) {
          payload.focus_questions.push(q);
        }
      });
    }

    return payload;
  }


  async function handleNewControlCreation() {
    let data = await controlForm.handleSubmit();

    if (data) {
      await handleControlFormClose();
      controlOptions.update(options => [...options, data]);
      $hazardStore.controls = [...$hazardStore.controls, data.id];
    }

  }

  const controlFormModalId = 'controlFormModalId';

  async function handleControlFormClose() {
    jQuery('#' + controlFormModalId).modal('hide');
  }

  // Stack control modal over the top of hazard modal https://stackoverflow.com/a/30876481/6487048
  jQuery(document).on('hidden.bs.modal', () => {
    if (jQuery('.modal:visible').length) {
      jQuery('body').addClass('modal-open');
    }
  });

</script>

<style>
  form :global(label) {
    font-weight: 500;
  }
  .footer {
    width: 100%;
  }

  .btn-link {
    width: fit-content;
    color: #656565;
    cursor: pointer;
  }

  .btn-link:focus {outline: none; color: #dd5900;}

  .btn-link:hover {
    color: #dd5900;
    text-decoration: none;
  }

  /* Enforces a minimum height so that when clicking through each step, the "next" button
     remains in the same spot allowing for quick movement through the form. */
  .consistent-height {
    min-height: 440px;
  }
</style>

<Modal
  {id}
  {title}
  onCancel={handleClose}
  onSave={handleSave}
  extraLarge={true}>
  <div slot="body">
    <form>
      <Stepper {steps} bind:currentStep />

      {#if currentStep === 1}
        <div class="consistent-height">
          <ComboSelect
              id="energy_types"
              label={sgettext("$$ENERGY_TYPE$$")}
              options={$energyTypeOptions}
              multiple={false}
              searchable={true}
              on:changed={getMechanismOptions}
              bind:value={$hazardStore.energy_type} />

          <br/>

          <ComboSelect
            id="mechanism_id"
            label={sgettext("$$MECHANISM$$")}
            bind:options={$mechanismOptions}
            bind:value={$hazardStore.mechanism_id}
            required="true"
            bind:isValid={steps[currentStep-1].fieldIsValid.mechanism_id.isValid}
            bind:errors={steps[currentStep-1].fieldIsValid.mechanism_id.errors}
            searchable={true}
            {schema} />

        </div>
      {:else if currentStep === 2}
        <div class="consistent-height">
          <div class="form-group">
            <DuplicateCheckTextInput
              id="name"
              required="true"
              apiurl={duplicateCheckApi}
              label={sgettext("$$RISK_NAME_1$$")}
              message={sgettext("$$SIMILAR_RISK_FOUND$$")}
              bind:value={$hazardStore.name}
              bind:isValid={steps[currentStep-1].fieldIsValid.name.isValid}
              bind:errors={steps[currentStep-1].fieldIsValid.name.errors}
              on:check={handleDuplicate}
              {schema} />
          </div>
          <div class="form-group" style="width: 100%">
            <ComboSelect
              id="icon_id"
              label={sgettext("$$RISK_ICON_1$$")}
              options={$iconOptions}
              searchable={true}
              bind:value={$hazardStore.icon_id}
              bind:isValid={steps[currentStep-1].fieldIsValid.icon_id.isValid}
              bind:errors={steps[currentStep-1].fieldIsValid.icon_id.errors}
              {schema}
              />
          </div>
          <div class="form-group">
            <TextInput
              id="description"
              label={sgettext("$$RISK_DESCRIPTION$$")}
              bind:isValid={steps[currentStep-1].fieldIsValid.description.isValid}
              bind:errors={steps[currentStep-1].fieldIsValid.description.errors}
              bind:value={$hazardStore.hazard}
              multiline={true}
              {schema} />
          </div>
          <div class="form-group">
            <FocusQuestionInput
              id="focusQuestionInput"
              label={sgettext("$$FOCUS_QUESTIONS$$")}
              bind:value={$hazardStore.focus_questions} />
          </div>
        </div>
      {:else if currentStep === 3}
        <div class="form-group consistent-height">

          <div class="d-flex p-3">
            <div>
              <GlobalControlSelect
                id="controls"
                label={sgettext("$$CHOOSE_EXISTING_CONTROLS$$")}
                options={$controlOptions}
                multiple={true}
                searchable={true}
                onCreateNewControl={handleShowControlForm}
                bind:value={$hazardStore.controls}
                let:item
              >
                <div slot="item-name">
                    <div class:text-danger={item.expired}>
                      <i class:d-none={!item.expired} class="fa fa-exclamation-circle"
                         title={sgettext('$$GLOBAL_CONTROL_EXPIRED_1$$')}></i>
                      {item.name}
                    </div>
                </div>
              </GlobalControlSelect>
            </div>
          </div>

        </div>
      {:else}
        <div class="form-group">
          <HazardDetail hazard={hazardDetail} />
          {#if $relatedActivities.hasOwnProperty('results') && $relatedActivities.results.length}
          <RelatedActivities data={$relatedActivities} />
          {/if}
        </div>
      {/if}
      {#if errors}
        <Errors {errors} />
      {/if}
    </form>
  </div>

  <div slot="footer" class="footer">
    <StepperNavigator
      bind:currentStep
      finalStep={steps.length}
      enableSaveAndAddAnother={true}
      bind:addAnother={addAnother}
      {isValid}
      {sending}
      back={() => errors = null}
      enableCancel={true}
      cancel={() => jQuery('#' + id).modal('hide')}
      next={handleFormValidation} />
  </div>
</Modal>

<Modal
  id={controlFormModalId}
  title={sgettext("$$NEW_GLOBAL_CONTROL$$")}
  onCancel={handleControlFormClose}
  onSave={handleNewControlCreation}
  backdrop={'static'}
  large={true}>
  <div slot="body">
        {#if showControlForm}
          <ControlInlineForm
            bind:this={controlForm}
            schema={controlSchema}
            bind:show={showControlForm}
            actionUrl={controlFormUrl}
            duplicateCheckApi={controlDuplicateCheckApi}
            bind:fieldIsValid={steps[currentStep-1].fieldIsValid}/>
        {/if}
  </div>
</Modal>
